import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { AdminAvatar } from "@/components/admin/avatar";
import { AdminButton } from "@/components/admin/button";
import { Card } from "@/components/admin/card";
import { ChipFilter } from "@/components/admin/chip-filter";
import { AdminIcon } from "@/components/admin/icon";
import { Tag } from "@/components/admin/tag";
import {
	useAdminRolesPermissions,
	useAdminUsersList,
} from "@/hooks/use-admin-users";
import type { AdminUserListItem } from "@/lib/admin/admin-users-api";
import { cn } from "@/lib/cn";

type StatusFilter = "" | "active" | "suspended" | "archived";

const STATUS_TAB: { k: StatusFilter; l: string }[] = [
	{ k: "", l: "Tous" },
	{ k: "active", l: "Actifs" },
	{ k: "suspended", l: "Suspendus" },
	{ k: "archived", l: "Archivés" },
];

interface UsersListProps {
	/** Empêche les actions header pour l'embed dans Administration */
	embed?: boolean;
}

export function UsersList({ embed = false }: UsersListProps) {
	const navigate = useNavigate();
	const [search, setSearch] = useState("");
	const [status, setStatus] = useState<StatusFilter>("");
	const [role, setRole] = useState<string>("");
	const [page, setPage] = useState(1);
	const pageSize = 25;

	const { data: rolesData } = useAdminRolesPermissions();
	const { data, isLoading } = useAdminUsersList({
		search: search || undefined,
		role: role || undefined,
		status: status || undefined,
		page,
		pageSize,
	});

	const items = data?.items ?? [];
	const total = data?.total ?? 0;
	const totalPages = Math.max(1, Math.ceil(total / pageSize));

	return (
		<Card>
			<div className="toolbar">
				<div
					style={{
						display: "flex",
						gap: 8,
						alignItems: "center",
						flexWrap: "wrap",
					}}
				>
					<div style={{ position: "relative" }}>
						<input
							data-testid="users-search"
							style={{
								height: 30,
								padding: "0 12px 0 30px",
								background: "#fff",
								border: "1px solid var(--a-border)",
								borderRadius: "var(--a-r-sm)",
								fontSize: 12,
								width: 220,
							}}
							placeholder="Rechercher email, nom..."
							value={search}
							onChange={(e) => {
								setSearch(e.target.value);
								setPage(1);
							}}
						/>
						<span
							style={{
								position: "absolute",
								left: 8,
								top: 7,
								color: "var(--a-ink-mute)",
							}}
						>
							<AdminIcon n="search" s={14} />
						</span>
					</div>

					{STATUS_TAB.map((t) => (
						<ChipFilter
							key={t.k}
							on={status === t.k}
							onClick={() => {
								setStatus(t.k);
								setPage(1);
							}}
						>
							{t.l}
						</ChipFilter>
					))}

					<select
						data-testid="users-role-filter"
						className="aform-i"
						style={{ width: 200, height: 30 }}
						value={role}
						onChange={(e) => {
							setRole(e.target.value);
							setPage(1);
						}}
					>
						<option value="">Tous les rôles</option>
						{rolesData?.roles.map((r) => (
							<option key={r.key} value={r.key}>
								{r.label}
							</option>
						))}
						<option value="super_admin">Super Admin</option>
					</select>
				</div>
				{!embed && (
					<div style={{ marginLeft: "auto" }}>
						<AdminButton
							variant="primary"
							size="sm"
							onClick={() => navigate("/administration/utilisateurs/nouveau")}
							data-testid="users-create"
						>
							<AdminIcon n="plus" s={12} />
							Nouvel utilisateur
						</AdminButton>
					</div>
				)}
			</div>

			<div className="table-wrap" style={{ margin: 0 }}>
				<table className="table">
					<thead>
						<tr>
							<th>Utilisateur</th>
							<th>Rôle</th>
							<th>Permissions+</th>
							<th>Statut</th>
							<th>Dernière connexion</th>
							<th>Créé</th>
							<th></th>
						</tr>
					</thead>
					<tbody data-testid="users-tbody">
						{isLoading && (
							<tr>
								<td colSpan={7} className="muted center">
									Chargement…
								</td>
							</tr>
						)}
						{!isLoading && items.length === 0 && (
							<tr>
								<td colSpan={7} className="muted center">
									Aucun utilisateur ne correspond aux filtres.
								</td>
							</tr>
						)}
						{items.map((u) => (
							<tr
								key={u.id}
								style={{ cursor: "pointer" }}
								onClick={() => navigate(`/administration/utilisateurs/${u.id}`)}
								data-testid={`user-row-${u.email}`}
							>
								<td>
									<div
										style={{ display: "flex", alignItems: "center", gap: 10 }}
									>
										<AdminAvatar name={u.fullName} />
										<div>
											<strong>{u.fullName}</strong>
											<div className="mono muted" style={{ fontSize: 11 }}>
												{u.email}
											</div>
										</div>
									</div>
								</td>
								<td>
									<RoleTag user={u} />
								</td>
								<td className="tn muted">
									{u.additionalPermissions.length > 0
										? `+${u.additionalPermissions.length}`
										: "—"}
								</td>
								<td>
									<StatusTag status={u.status} />
								</td>
								<td className="muted" style={{ fontSize: 11 }}>
									{u.lastLoginAt
										? new Date(u.lastLoginAt).toLocaleString("fr-FR")
										: "—"}
								</td>
								<td className="muted" style={{ fontSize: 11 }}>
									{new Date(u.createdAt).toLocaleDateString("fr-FR")}
								</td>
								<td onClick={(e) => e.stopPropagation()}>
									<button className="top-btn">
										<AdminIcon n="dots" s={14} />
									</button>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			<div className="pagi" style={{ padding: "12px 18px" }}>
				<div>
					Affichage {items.length === 0 ? 0 : (page - 1) * pageSize + 1} –{" "}
					{(page - 1) * pageSize + items.length} sur{" "}
					<strong className="tn">{total}</strong>
				</div>
				<div className="pages">
					<button
						onClick={() => setPage((p) => Math.max(1, p - 1))}
						disabled={page === 1}
					>
						<AdminIcon n="chevL" s={12} />
					</button>
					{Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map(
						(p) => (
							<button
								key={p}
								className={cn(p === page && "on")}
								onClick={() => setPage(p)}
							>
								{p}
							</button>
						),
					)}
					<button
						onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
						disabled={page === totalPages}
					>
						<AdminIcon n="chevR" s={12} />
					</button>
				</div>
			</div>
		</Card>
	);
}

function RoleTag({ user }: { user: AdminUserListItem }) {
	const variant =
		user.role.key === "super_admin"
			? "terra"
			: user.role.key === "admin"
				? "info"
				: user.role.key.startsWith("manager_")
					? "success"
					: "neutral";
	return <Tag variant={variant}>{user.role.label}</Tag>;
}

function StatusTag({
	status,
}: {
	status: "active" | "suspended" | "archived";
}) {
	if (status === "active") return <Tag variant="success">Actif</Tag>;
	if (status === "suspended") return <Tag variant="warning">Suspendu</Tag>;
	return <Tag variant="neutral">Archivé</Tag>;
}
